import { useEffect, useRef, useState } from "react";

const A04 = "https://storage.helloreaddy.io/project_files/7dabfe77-b738-4536-b87c-583d4a2d32dc/596cbfc0-caaa-479f-b94f-734aa7c0487c_compressed_ChatGPT-Image-2026922-10_17_19.webp";
const A09 = "https://storage.helloreaddy.io/project_files/7dabfe77-b738-4536-b87c-583d4a2d32dc/55c889e0-7ff5-4554-ab70-2bee5bbca332_compressed_ChatGPT-Image-2026922-10_17_38.webp";

const clamp01 = (v: number) => Math.min(1, Math.max(0, v));
const easeInOut = (t: number) =>
  t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;

export default function TransitionTest() {
  const trackRef = useRef<HTMLDivElement>(null);
  const [p, setP] = useState(0);
  const [vw, setVw] = useState(1440);
  const [vh, setVh] = useState(900);

  useEffect(() => {
    const upd = () => {
      setVw(window.innerWidth);
      setVh(window.innerHeight);
    };
    upd();
    window.addEventListener("resize", upd);
    return () => window.removeEventListener("resize", upd);
  }, []);

  useEffect(() => {
    const onScroll = () => {
      const el = trackRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const total = el.offsetHeight - window.innerHeight;
      setP(clamp01(-rect.top / Math.max(1, total)));
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Timeline (progress 0 → 1)
  // 0.00 – 0.15  Phase 1: full-bleed hold
  // 0.15 – 0.55  Phase 2: outer container shrinks to editorial 4:3
  // 0.55 – 0.65  hold
  // 0.65 – 1.00  Phase 3: A09 rises from translateY(100%) → 0
  const shrink = easeInOut(clamp01((p - 0.15) / 0.4));
  const rise = easeInOut(clamp01((p - 0.65) / 0.35));

  const editorialW = Math.min(1120, vw - (vw >= 1024 ? 128 : 48));
  const editorialH = (editorialW * 3) / 4;
  const scaleFit = Math.min(1, (vh - 160) / editorialH);
  const finalW = editorialW * scaleFit;
  const finalH = editorialH * scaleFit;
  const finalLeft = (vw - finalW) / 2;
  const finalTop = (vh - finalH) / 2;

  const cW = vw - shrink * (vw - finalW);
  const cH = vh - shrink * (vh - finalH);
  const cLeft = shrink * finalLeft;
  const cTop = shrink * finalTop;

  return (
    <div className="min-h-screen bg-[#F3EEE7] text-[#1D1A17]">
      <div className="h-[80vh] flex items-center justify-center px-6">
        <div className="max-w-xl">
          <div className="text-[11px] tracking-[0.2em] uppercase opacity-60">
            Isolated Test
          </div>
          <h1 className="mt-4 text-4xl md:text-5xl font-light leading-tight">
            Ridge House transition — pinned stage
          </h1>
          <p className="mt-6 opacity-70 leading-relaxed">
            Scroll down. The stage below pins for 3× viewport. A04 fills the
            screen, its outer frame shrinks into a centered 4:3 editorial
            container, then A09 rises from below to cover the same pinned
            viewport. The stage itself never moves.
          </p>
        </div>
      </div>

      {/* SCROLL TRACK */}
      <div ref={trackRef} className="relative" style={{ height: "300vh" }}>
        <div
          className="sticky top-0 w-full overflow-hidden bg-[#F3EEE7]"
          style={{ height: "100svh" }}
        >
          {/* A04 outer container animates its own geometry */}
          <div
            className="absolute overflow-hidden bg-black"
            style={{
              top: `${cTop}px`,
              left: `${cLeft}px`,
              width: `${cW}px`,
              height: `${cH}px`,
              willChange: "top,left,width,height",
            }}
          >
            <img
              src={A04}
              alt="A04"
              className="block w-full h-full"
              style={{ objectFit: "cover", objectPosition: "center" }}
            />
          </div>

          {/* A09 rises from below, covers pinned viewport */}
          <div
            className="absolute inset-0 overflow-hidden"
            style={{
              transform: `translateY(${(1 - rise) * 100}%)`,
              willChange: "transform",
            }}
          >
            <img
              src={A09}
              alt="A09"
              className="block w-full h-full"
              style={{ objectFit: "cover", objectPosition: "center" }}
            />
          </div>

          {/* HUD */}
          <div className="absolute top-4 left-4 text-[11px] tracking-[0.18em] uppercase bg-black/60 text-white px-3 py-1.5 rounded-sm pointer-events-none">
            progress {(p * 100).toFixed(0)}% · shrink {(shrink * 100).toFixed(0)}% · rise {(rise * 100).toFixed(0)}%
          </div>
        </div>
      </div>

      <div className="h-[80vh] flex items-center justify-center px-6">
        <div className="max-w-xl text-center">
          <div className="text-[11px] tracking-[0.2em] uppercase opacity-60">
            End of test
          </div>
          <p className="mt-4 opacity-70">
            Normal document scroll resumes here — the pinned stage has released.
          </p>
        </div>
      </div>
    </div>
  );
}